import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Document, Page } from 'react-pdf';
import { Box, Button, Card, Stack, Typography } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import FilePreviewDialog from './FilePreviewDialog';
import { buildSafeDownloadFilename } from './fileVisuals';
import {
  parseEdmIdFromDownloadEdmUrl,
  resolveEdmWithAgentPreviewUrl,
} from '../services/api';
import ShareAssetButton from '../../shared/components/ShareAssetButton';
import type { ShareAssetKind } from '../../shared/components/ShareAssetEmailDialog';

// ---------------------------------------------------------------------------
// HighlightCard — the card used by the "Latest Highlights" rail on the
// Highlights & eBook page and by the "Recent Highlights & eBook" rail
// on the Dashboard. Extracted so both surfaces render identically.
//
// The card cover is a live first-page preview of the PDF rendered via
// pdf.js, with a small "PDF" badge in the corner. The placeholder
// image is kept around purely as a graceful fallback when the
// document can't be fetched (404, CORS) or rendered.
// ---------------------------------------------------------------------------

/** Card-ready highlight shape produced by both consuming pages. */
export interface HighlightItem {
  id: string;
  title: string;
  /** Pre-formatted human date, e.g. "08 May 2026". */
  date: string;
  /** Free-form badge under the title (e.g. launcher Type, "PDF"). */
  duration: string;
  /** Generated cover image used as fallback when the PDF can't render. */
  image: string;
  /** Curated thumbnail (`EDM.Thumbnail` or `ProjectLauncher.ThumbnailPath`). */
  cover?: string | null;
  /** Real PDF URL — used both as Download target and pdf.js source. */
  url: string;
  /**
   * Optional public (no-agent-details) variant of the download URL.
   * When provided the shared FilePreviewDialog surfaces a "With
   * Agent Details" checkbox; when omitted the toggle is hidden.
   */
  urlPublic?: string | null;
  /** Suggested save-as filename for the Download button. */
  filename: string;
  /** @deprecated Unused — kept optional for API payload compatibility. */
  downloads?: number;
}

// Cover renderer that rasterises the first page of the PDF via pdf.js
// and falls back to the placeholder image if the file can't be fetched
// or rendered (e.g. 404, CORS, malformed PDF). Loading is deferred
// until the card is near the viewport so a five-card carousel doesn't
// hammer the network on mount. Exported so `PosterCard` can reuse the
// same preview treatment for eBook covers without adopting the full
// Highlight card chrome.
export function HighlightPdfCover({
  url,
  fallbackImage,
  alt,
  height = { xs: 200, sm: 220, md: 240 },
  frameless = false,
}: {
  url: string;
  fallbackImage: string;
  alt: string;
  height?: { xs: number; sm: number; md: number };
  /** Full-bleed cover (no inner padding) — matches `PosterCard` EDM thumbnails. */
  frameless?: boolean;
}) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [inView, setInView] = useState(false);
  const [failed, setFailed] = useState(false);
  const [resolvedPdfUrl, setResolvedPdfUrl] = useState<string | null>(null);

  const usable = !!url && url !== '#';
  const edmId = useMemo(() => parseEdmIdFromDownloadEdmUrl(url), [url]);

  useEffect(() => {
    if (!usable) return;
    const node = containerRef.current;
    if (!node) return;
    if (typeof IntersectionObserver === 'undefined') {
      setInView(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setInView(true);
            observer.disconnect();
            break;
          }
        }
      },
      // 200px lookahead so the page is ready by the time the user
      // scrolls/swipes the card into view.
      { rootMargin: '200px' },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [usable]);

  useEffect(() => {
    if (!usable || !inView) {
      setResolvedPdfUrl(null);
      return;
    }
    if (!edmId) {
      setResolvedPdfUrl(url);
      return;
    }

    let cancelled = false;
    setResolvedPdfUrl(null);
    resolveEdmWithAgentPreviewUrl(edmId)
      .then((previewUrl) => {
        if (!cancelled) setResolvedPdfUrl(previewUrl);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });

    return () => {
      cancelled = true;
    };
  }, [usable, inView, edmId, url]);

  const pdfSource = resolvedPdfUrl;
  const showPreview = usable && !failed && Boolean(pdfSource);

  // Fallback image shares the same fit rules as the live pdf.js
  // canvas: `object-fit: contain` so portrait images aren't cropped,
  // sized to the available width/height with a max so they don't
  // overflow the inner padding.
  const fallbackImgSx = frameless
    ? {
        width: '100%',
        height: '100%',
        objectFit: 'cover' as const,
        display: 'block',
      }
    : {
        maxWidth: '100%',
        maxHeight: '100%',
        width: 'auto',
        height: 'auto',
        objectFit: 'contain' as const,
        display: 'block',
        borderRadius: 1,
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.12)',
      };

  return (
    <Box
      ref={containerRef}
      sx={{
        width: '100%',
        height,
        maxHeight: height,
        minHeight: 0,
        flexShrink: 0,
        // Soft frame around the document. The neutral surface tone
        // shows through as margin on both portrait (vertical bars on
        // the sides) and landscape (horizontal bars on top/bottom)
        // documents so neither orientation ever gets clipped.
        bgcolor: frameless ? 'transparent' : 'var(--color-surface-elevated)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        // Inner breathing room so the document never butts against
        // the card edges. Matches the "framed document" look from
        // the legacy portal's launcher cards.
        p: frameless ? 0 : { xs: 1, sm: 1.25 },
        // react-pdf wraps the canvas in two layout-participating divs
        // (`.react-pdf__Document` and `.react-pdf__Page`) that take on
        // the rasterised page's intrinsic dimensions (e.g. 800×1131
        // for an A4 portrait). Those wrappers intercept the height
        // constraint before it ever reaches the canvas, so a portrait
        // page renders at full width and gets clipped vertically.
        // Setting `display: contents` removes the wrappers from the
        // box tree so the canvas becomes a direct flex child of this
        // outer Box — its `max-width: 100%` / `max-height: 100%` then
        // resolve against the real 200–240px tall frame and the page
        // scales-to-fit in both orientations.
        '& .react-pdf__Document, & .react-pdf__Page': {
          display: 'contents !important',
          background: 'transparent !important',
        },
        '& canvas': {
          maxWidth: '100% !important',
          maxHeight: '100% !important',
          width: 'auto !important',
          height: 'auto !important',
          minHeight: '0 !important',
          display: 'block',
          borderRadius: frameless ? 0 : 4,
          boxShadow: frameless ? 'none' : '0 4px 12px rgba(0, 0, 0, 0.12)',
        },
      }}
    >
      {showPreview && inView ? (
        <Document
          file={pdfSource}
          onLoadError={() => setFailed(true)}
          onSourceError={() => setFailed(true)}
          loading={<Box component="img" src={fallbackImage} alt={alt} sx={fallbackImgSx} />}
          error={<Box component="img" src={fallbackImage} alt={alt} sx={fallbackImgSx} />}
          noData={<Box component="img" src={fallbackImage} alt={alt} sx={fallbackImgSx} />}
        >
          <Page
            pageNumber={1}
            // Render at higher than display width so the canvas stays
            // crisp on retina/HiDPI screens after CSS scales it down.
            width={800}
            renderAnnotationLayer={false}
            renderTextLayer={false}
            onRenderError={() => setFailed(true)}
            canvasBackground="#fff"
          />
        </Document>
      ) : (
        // Either we haven't entered the viewport yet, the URL is a
        // placeholder, or pdf.js gave up — either way show the
        // generated cover image so the rail is never blank.
        <Box component="img" src={fallbackImage} alt={alt} sx={fallbackImgSx} />
      )}
    </Box>
  );
}

/**
 * Card used by both the Highlights & eBook page's "Latest Highlights"
 * rail and the Dashboard's "Recent Highlights & eBook" rail. Renders a
 * live PDF preview as the cover, a "PDF" badge, the title/date/downloads
 * meta strip, and a Download button.
 */
export function HighlightCard({
  item,
  shareKind = 'ebook',
}: {
  item: HighlightItem;
  shareKind?: ShareAssetKind;
}) {
  const [previewOpen, setPreviewOpen] = useState(false);
  const [coverFailed, setCoverFailed] = useState(false);
  const curatedCover = item.cover && !coverFailed ? item.cover : null;

  useEffect(() => {
    setCoverFailed(false);
  }, [item.cover]);

  const handleOpenPreview = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setPreviewOpen(true);
  };
  return (
    <>
    <Card
      sx={{
        width: '100%',
        bgcolor: 'var(--color-surface)',
        color: 'var(--color-text)',
        borderRadius: 2,
        overflow: 'hidden',
        boxShadow: 'var(--color-shadow-card)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Cover area is wrapped in a real <button> so a click anywhere
          on the PDF preview / fallback image opens the same preview
          dialog the Download CTA does. `all: 'unset'` strips the
          default browser chrome and `display: block` collapses the
          inline-button baseline, so the wrapped HighlightPdfCover
          renders edge-to-edge inside the card with no visual change
          besides the pointer cursor on hover. */}
      <Box
        component="button"
        type="button"
        onClick={handleOpenPreview}
        aria-label={`Open preview for ${item.title}`}
        sx={{
          all: 'unset',
          display: 'block',
          position: 'relative',
          width: '100%',
          cursor: 'pointer',
          '&:focus-visible': {
            outline: '2px solid var(--color-accent)',
            outlineOffset: -2,
          },
        }}
      >
        {/* Curated thumbnail when available; otherwise live PDF preview. */}
        {curatedCover ? (
          <Box
            component="img"
            src={curatedCover}
            alt={item.title}
            onError={() => setCoverFailed(true)}
            sx={{
              width: '100%',
              height: { xs: 200, sm: 220, md: 240 },
              objectFit: 'contain',
              display: 'block',
              bgcolor: 'var(--color-surface-elevated)',
            }}
          />
        ) : (
          <HighlightPdfCover
            url={item.url}
            fallbackImage={item.image}
            alt={item.title}
          />
        )}
        {/* PDF badge — signals the file format at a glance. */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 8,
            right: 8,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            px: 0.85,
            height: 22,
            borderRadius: 999,
            bgcolor: 'rgba(0,0,0,0.65)',
            color: '#fff',
            fontWeight: 700,
            fontSize: '0.7rem',
            letterSpacing: '0.04em',
          }}
        >
          <PictureAsPdfIcon sx={{ fontSize: '0.95rem' }} />
          PDF
        </Box>
      </Box>
      <Stack spacing={0.75} sx={{ p: { xs: 1.25, sm: 1.5 }, minWidth: 0, flex: 1 }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={0.5}
          sx={{ minWidth: 0 }}
        >
          <Typography
            variant="caption"
            sx={{
              color: 'var(--color-text-muted)',
              fontWeight: 600,
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {item.date}
          </Typography>
          {item.url && item.url !== '#' ? (
            <ShareAssetButton
              visible
              asset={{
                id: item.id,
                kind: shareKind,
                title: item.title || item.id,
                filename: item.filename,
                url: item.url,
              }}
            />
          ) : null}
        </Stack>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 700,
            color: 'var(--color-text-strong)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            minWidth: 0,
          }}
          title={item.title}
        >
          {item.title}
        </Typography>
        <Button
          onClick={handleOpenPreview}
          variant="contained"
          size="small"
          disableElevation
          startIcon={<DownloadIcon />}
          fullWidth
          sx={{
            mt: 0.5,
            textTransform: 'none',
            fontWeight: 700,
            borderRadius: 999,
            bgcolor: 'var(--color-accent)',
            color: '#fff',
            '&:hover': { bgcolor: 'var(--color-accent-strong)' },
          }}
        >
          Download
        </Button>
      </Stack>
    </Card>
    <FilePreviewDialog
      open={previewOpen}
      onClose={() => setPreviewOpen(false)}
      title={item.title}
      url={item.url}
      urlPublic={item.urlPublic ?? null}
      filename={buildSafeDownloadFilename(item.title, item.filename)}
    />
    </>
  );
}

export default HighlightCard;
